import React, { useEffect, useState, useRef } from "react";
import style from "../Style.module.scss";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import StatusDot from "../../../components/StatusDot/StatusDot";
import CaretDown from "../../../assets/svg/icons/common/CaretDown";
import Download from "../../../assets/svg/icons/common/Download";
import ExpandDetailsIcon from "../../../assets/svg/icons/common/ExpandDetailsIcon";
import CollapseDetailsIcon from "../../../assets/svg/icons/common/CollapseDetailsIcon";
import CloseIcon24 from "../../../assets/svg/icons/common/CloseIcon24";
import ChevronLeft16 from "../../../assets/svg/icons/common/ChevronLeft16";
import TrendUp from "../../../assets/svg/icons/common/TrendUp";
import TrendDown from "../../../../v2/assets/svg/icons/common/TrendDown";
import Tabs from "../../../components/Tabs/Tabs";
import Sales from "./Sales/Sales";
import Detail from "./Details/Details";
import Activity from "../../application/components/Activity/Activity";
import Pricing from "./Pricing/Pricing";
import Chargeback from "./Chargeback/Chargeback";
import Residuals from "./Residuals";
import useCommonStore from "../../../stores/common";
import useMerchantStore from "../../../stores/merchant";
import useDomainStore from "../../../stores/domain";
import useUserStore from "../../../stores/user";
import moment from "moment";
import {
  getPreappById,
  getMerchantInfo,
  getMerchantStatement,
  getMerchantDetailWithTrends,
} from "../../../../api";
import MonthPicker from "../../../components/MonthPicker/Index";
import toast from "react-hot-toast";
import { MonthPicker as CalendarFilter } from "../../../components/form/month-picker";
import {
  parse,
  endOfMonth,
  format,
  startOfYear,
  endOfYear,
  subYears,
  startOfMonth,
} from "date-fns";
import { dateFormatter } from "../../../../components/helper/helper";
import DetailsSkeleton from "./Details/DetailsSkeleton";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

// format number into K, M, B, T
const formatNumber = (number) => {
  if (number >= 1000000000) {
    return (number / 1000000000).toFixed(1) + "B";
  } else if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + "M";
  } else if (number >= 1000) {
    return (number / 1000).toFixed(1) + "K";
  }
  return number;
};

const isNumericStatValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return trimmedValue !== "" && Number.isFinite(Number(trimmedValue));
  }

  return false;
};

const MainTabContent = ({ merchantDetails, canViewResidualData }) => {
  return (
    <div className={style.mainTabContent}>
      <div className={style.stats_grid}>
        {[
          {
            value: merchantDetails?.sales?.current_month || 0,
            label: "Monthly Sales",
            trend:
              (merchantDetails?.sales?.current_month || 0) ===
              (merchantDetails?.sales?.last_month || 0)
                ? null
                : merchantDetails?.sales?.current_month >
                    merchantDetails?.sales?.last_month
                  ? "up"
                  : "down",
          },
          {
            value: merchantDetails?.volume_cap || 0,
            label: "Monthly Cap",
          },
          {
            value: merchantDetails?.chargebacks?.current_month || 0,
            label: "Chargebacks",
            trend:
              (merchantDetails?.chargebacks?.current_month || 0) ===
              (merchantDetails?.chargebacks?.last_month || 0)
                ? null
                : merchantDetails?.chargebacks?.current_month >
                    merchantDetails?.chargebacks?.last_month
                  ? "up"
                  : "down",
          },
          {
            value: merchantDetails?.refund?.current_month || 0,
            label: "Refunds",
            trend:
              (merchantDetails?.refund?.current_month || 0) ===
              (merchantDetails?.refund?.last_month || 0)
                ? null
                : merchantDetails?.refund?.current_month >
                    merchantDetails?.refund?.last_month
                  ? "up"
                  : "down",
          },
          // Same gate as the Residuals tab — hidden rather than shown at $0
          // for users not configured for residual data.
          ...(canViewResidualData
            ? [
                {
                  value: merchantDetails?.reserve || 0,
                  label: "Reserve Balance",
                },
                {
                  value: merchantDetails?.residual || 0,
                  label: "Residual (last month)",
                },
              ]
            : []),
        ].map((stat, index) => (
          <div key={index} className={style.stat_card}>
            <div className={style.stat_value}>
              <span
                className={style.stat_value_text}
                title={String(stat.value ?? "-")}
              >
                {isNumericStatValue(stat.value)
                  ? `$ ${formatNumber(Number(stat.value))}`
                  : String(stat.value ?? "-")}
              </span>
              {stat.trend && (
                <div className={`${style.trend} ${style[stat.trend]}`}>
                  {stat.trend === "up" ? (
                    <TrendUp size={16} />
                  ) : (
                    <TrendDown size={16} />
                  )}
                </div>
              )}
            </div>
            <div className={style.stat_label}>
              <span>{stat.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function Details({ isDetailsOnly = false }) {
  const { isKurvDomain } = useDomainStore();
  const toggleShowDetails = useCommonStore((state) => state.toggleShowDetails);
  const toggleExpandDetails = useCommonStore(
    (state) => state.toggleExpandDetails,
  );
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const setDateRange = useCommonStore((state) => state.setDateRange);
  const dateRange = useCommonStore((state) => state.dateRange);

  const activeAppId = useMerchantStore((state) => state.activeAppId);
  const activeApp = useMerchantStore((state) => state.activeApp);
  const setActiveApp = useMerchantStore((state) => state.setActiveApp);
  const canViewResidualData = useUserStore(
    (state) => state.canViewResidualData,
  );
  const [loading, setLoading] = useState(true);
  const statsContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const isMobile = useMediaQuery(768);

  const tabs = [
    {
      label: "Main",
      content: (
        <MainTabContent
          merchantDetails={merchantDetails}
          canViewResidualData={canViewResidualData}
        />
      ),
      badge: null,
      mobileOnly: true,
    },
    { label: "Sales", content: <Sales /> },
    { label: "Chargebacks", content: <Chargeback /> },
    { label: "Details", content: <Detail /> },
    {
      label: "Pricing",
      content: <Pricing />,
    },
    // Hidden rather than rendered-then-403'd for users who are not configured
    // for residual data. Per-merchant access is still enforced server-side.
    ...(canViewResidualData
      ? [
          {
            label: "Residuals",
            content: <Residuals />,
          },
        ]
      : []),
    {
      label: "Activity",
      content: <Activity activeApp={activeApp} />,
    },
  ];

  const handleTabSelect = (index) => {
    // console.log(`Selected Tab: ${index}`);
  };

  const onCloseClick = () => {
    toggleShowDetails();

    if (expandDetails) {
      toggleExpandDetails();
    }
  };

  useEffect(() => {
    if (!activeAppId) return;
    setLoading(true);
    let isCurrent = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        // NOTE: Add abort controller down the line or even debounce
        const [preappResponse, merchantResponse, merchantDetailWithTrends] =
          await Promise.all([
            getPreappById({ id: activeAppId }),
            getMerchantInfo({ id: activeAppId }),
            getMerchantDetailWithTrends({ powerapp_id: activeAppId }),
          ]);

        if (
          !preappResponse.status ||
          !merchantResponse.status ||
          !merchantDetailWithTrends.status
        ) {
          console.error("Failed fetching data");
          return;
        }

        if (!isCurrent) {
          return;
        }

        setActiveApp({
          ...preappResponse.data,
          bankinfo: merchantResponse.bankinfo,
          creditcardprocess: merchantResponse.creditcardprocess,
          powerappbusinessprofile: merchantResponse.powerappbusinessprofile,
          powerappequipment: merchantResponse.powerappequipment,
          prices: merchantResponse.prices,
          signers: merchantResponse.signers,
          billing: merchantResponse.billing,
          powerappReferral: merchantResponse.powerappReferral,
          merchant_details:
            merchantDetailWithTrends?.data?.merchant_details ?? {},
        });
        setMerchantDetails(merchantDetailWithTrends?.data);
        setLoading(false);
      } catch (err) {
        if (isCurrent) {
          console.error("Error fetching data:", err);
        }
      }
    };

    fetchData();

    return () => {
      isCurrent = false;
    };
  }, [activeAppId]);

  const getFullMonthName = (shortMonth) => {
    const date = new Date(`${shortMonth} 1, 2020`);
    const options = { month: "long" };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const handleDownload = (base64Pdf, file_name) => {
    const byteCharacters = Buffer.from(base64Pdf, "base64");
    const blob = new Blob([byteCharacters], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = file_name;
    link.click();

    window.URL.revokeObjectURL(url);
  };

  const downloadStatement = (data) => {
    const toastId = toast.loading(
      "Your statement is being run, do not close the page. The file will be downloaded automatically",
      { duration: 5 * 60 * 1000 },
    );
    getMerchantStatement({
      powerapp_id: activeApp.id,
      month: getFullMonthName(data.month),
      year: data.year,
    })
      .then((response) => {
        if (response.status) {
          handleDownload(
            response.data,
            `Merchant_Statement_${data.year}_${getFullMonthName(data.month)}.pdf`,
          );
          toast.success("Statement downloaded successfully", {
            id: toastId,
            duration: 30 * 1000,
          });
        } else {
          toast.error(response.message, { id: toastId, duration: 30 * 1000 });
        }
      })
      .catch((error) => {
        toast.error("Something went wrong!", {
          id: toastId,
          duration: 30 * 1000,
        });
      });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - statsContainerRef.current.offsetLeft);
    setScrollLeft(statsContainerRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - statsContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    statsContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMonthPickerChange = (data) => {
    let startDate, endDate;
    const currentDate = new Date();
    let rangeType = "MONTH";

    if (["MTD", "LTY", "YTD"].includes(data.month)) {
      rangeType = data.month;
      if (data.month === "YTD") {
        startDate = startOfYear(currentDate);
        endDate = currentDate;
      } else if (data.month === "MTD") {
        startDate = startOfMonth(currentDate);
        endDate = currentDate;
      } else if (data.month === "LTY") {
        const lastYearDate = subYears(currentDate, 1);
        startDate = startOfYear(lastYearDate);
        endDate = endOfYear(lastYearDate);
      }
    } else {
      const parsedDate = parse(
        `${data.month} ${data.year}`,
        "MMM yyyy",
        new Date(),
      );
      startDate = parsedDate;
      endDate = endOfMonth(parsedDate);
    }

    setDateRange({
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
      rangeType,
    });
  };

  return (
    <div className={style.details_container}>
      {!loading && (
        <>
          <div className={style.details_head}>
            {isDetailsOnly ? (
              <button
                className={style.expand_button}
                onClick={() => window.history.back()}
              >
                <ChevronLeft16 />
                <span style={{ color: isKurvDomain ? "#22A276" : "black" }}>
                  Back
                </span>
              </button>
            ) : (
              <>
                <button
                  className={style.expand_button}
                  onClick={() => {
                    toggleExpandDetails();
                  }}
                >
                  {!expandDetails ? (
                    <ExpandDetailsIcon
                      fill={isKurvDomain ? "#22A276" : "#254FE2"}
                    />
                  ) : (
                    <CollapseDetailsIcon
                      fill={isKurvDomain ? "#22A276" : "#254FE2"}
                    />
                  )}
                  <span style={{ color: isKurvDomain ? "#22A276" : "#254FE2" }}>
                    {!expandDetails ? "Expand Details" : "Minimize"}
                  </span>
                </button>

                <ButtonIcon onClick={onCloseClick}>
                  <CloseIcon24 />
                </ButtonIcon>
              </>
            )}
          </div>

          <div className={style.application_min_data}>
            <div className={style.container_min_data}>
              <div className={style.left}>
                <p className={style.heading}>{activeApp?.field_dba}</p>
                <div className={style.status_desc}>
                  <StatusDot color="#37B450" />
                  <span>
                    Open since{" "}
                    {merchantDetails?.date_approved
                      ? dateFormatter(merchantDetails.date_approved)
                      : "-"}
                  </span>
                </div>
              </div>

              <div className={style.right}>
                <MonthPicker
                  onSubmit={(data) => downloadStatement(data)}
                  submitText="Download PDF"
                  text={
                    !expandDetails ? (
                      <div className={style.minimized_button}>
                        <Download />
                        <CaretDown />
                      </div>
                    ) : (
                      <>
                        <Download />
                        <span>Statement</span>
                        <CaretDown />
                      </>
                    )
                  }
                />

                <CalendarFilter
                  variant="default"
                  onSubmit={handleMonthPickerChange}
                  changeOnSubmit
                  showYearFilters
                  showActions
                />

                {/* <DateRangePicker
                  align="right"
                  variant={!expandDetails ? "icon" : "largeFilter"}
                  value={dateRange}
                  onChange={(range) => setDateRange(range)}
                  placeholder={!expandDetails ? "" : "Select date range"}
                  className={!expandDetails ? style.minimized_button : ""}
                /> */}
              </div>
            </div>
          </div>

          <div
            ref={statsContainerRef}
            className={`${style.stats_container} ${isDragging ? style.grabbing : ""}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {[
              {
                value: merchantDetails?.sales?.current_month || 0,
                label: "Monthly Sales",
                trend:
                  (merchantDetails?.sales?.current_month || 0) ===
                  (merchantDetails?.sales?.last_month || 0)
                    ? null
                    : merchantDetails?.sales?.current_month >
                        merchantDetails?.sales?.last_month
                      ? "up"
                      : "down",
              },
              {
                value: merchantDetails?.volume_cap || 0,
                label: "Monthly Cap",
              },
              {
                value: merchantDetails?.chargebacks?.current_month || 0,
                label: "Chargebacks",
                trend:
                  (merchantDetails?.chargebacks?.current_month || 0) ===
                  (merchantDetails?.chargebacks?.last_month || 0)
                    ? null
                    : merchantDetails?.chargebacks?.current_month >
                        merchantDetails?.chargebacks?.last_month
                      ? "up"
                      : "down",
              },
              {
                value: merchantDetails?.refund?.current_month || 0,
                label: "Refunds",
                trend:
                  (merchantDetails?.refund?.current_month || 0) ===
                  (merchantDetails?.refund?.last_month || 0)
                    ? null
                    : merchantDetails?.refund?.current_month >
                        merchantDetails?.refund?.last_month
                      ? "up"
                      : "down",
              },
              // Same gate as the Residuals tab — hidden rather than shown at
              // $0 for users not configured for residual data.
              ...(canViewResidualData
                ? [
                    {
                      value: merchantDetails?.reserve || 0,
                      label: "Reserve Balance",
                    },
                    {
                      value: merchantDetails?.residual || 0,
                      label: "Residual (last month)",
                    },
                  ]
                : []),
            ].map((stat, index) => (
              <div
                key={index}
                className={`${style.stat_item} ${expandDetails ? style.expanded_item : ""}`}
              >
                <div className={style.counter}>
                  <span
                    className={style.counter_value}
                    title={String(stat.value ?? "-")}
                  >
                    {isNumericStatValue(stat.value)
                      ? `$ ${formatNumber(Number(stat.value))}`
                      : String(stat.value ?? "-")}
                  </span>
                  {stat.trend && (
                    <div className={`${style.trend} ${style[stat.trend]}`}>
                      {stat.trend === "up" ? (
                        <TrendUp size={16} />
                      ) : (
                        <TrendDown size={16} />
                      )}
                    </div>
                  )}
                </div>
                <div className={style.stat_desc}>
                  <span>{stat.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Tabs
              tabs={tabs}
              onSelect={handleTabSelect}
              active_index={isMobile ? 0 : 1}
              isMobile
            />
          </div>
        </>
      )}

      {loading && <DetailsSkeleton />}
    </div>
  );
}

export default Details;
